import { CardsData } from "../data/balance";
import { CardKind } from "./entities";
import type { PaceStep } from "./systems/draftFlow";

/**
 * What an in-run hand deals (DESIGN §4.4), decided when the offer opens from its pacing and what the board can take.
 * Pure, so the HUD looks ahead with exactly the plan the sim will deal (cardPacing.ts).
 *
 * Every hand is a real choice (2026-09-25, Kevin: "we should never offer only 1 option, or offer only to speed up the
 * next card"): at least MIN_CHOICE road, tower or upgrade cards. When only one is due — an Upgrade alone, the one road
 * shape a crowded map leaves, or the one tower type left — the hand BORROWS the next due card of another kind: road
 * while the road can grow, else a tower, else the Upgrade (road first: it is free to take and leaves the scarce tower
 * pace, and the HUD's count of it, alone). A borrowed card that is taken is that pace's next card taken early: its count
 * goes back by the offers it was early (draftFlow.paceAfterTaking), so the next one comes a full interval after the one
 * it replaced, and a kind already taken early is not borrowed again until its pace catches up. Taking the other card
 * leaves every pace as it was. When no kind can be borrowed, the offer does not show (its points roll on) — never a
 * lone card.
 */

/** A hand needs this many real cards (road, tower or upgrade) to be a choice; SKIP is not one. */
export const MIN_CHOICE = 2;
/** Cards a borrowed kind adds: one, the top-up to MIN_CHOICE. */
const BORROWED_CARDS = 1;

/** What the board can take now: tower types in the pool, road shapes with a fit, whether some tower can take a tier. */
export interface HandSupply { readonly towerTypes: number; readonly roadShapes: number; readonly upgradeable: boolean }
/** The pace intervals read now (towers at the towers standing, road at the road pace laid). */
export interface PaceIntervals { readonly towers: number; readonly upgrade: number; readonly road: number }

export interface HandPlan {
  /** Tower cards (different types), the Upgrade card, road cards (different shapes) — the due cards and any borrowed one. */
  readonly towers: number;
  readonly upgrade: boolean;
  readonly road: number;
  /** The kind topped up ahead of its pace, or null. */
  readonly borrowed: CardKind | null;
  /** How many offers early the borrowed card is (≥ 1): taking it sets that pace's count back by this many. */
  readonly borrowedEarly: number;
}

/** Road, tower and upgrade cards in the hand. */
export function realCards(plan: HandPlan): number { return plan.towers + (plan.upgrade ? 1 : 0) + plan.road; }

/**
 * The hand an in-run offer with this pacing deals, or null when it does not show (nothing due, or a lone card with
 * nothing to borrow beside it). Road fills the slots the tower and upgrade cards leave, as before.
 */
export function planHand(cards: CardsData, step: PaceStep, intervals: PaceIntervals, supply: HandSupply): HandPlan | null {
  const upgrade = step.upgradeDue && supply.upgradeable;
  // M38 (more tower cards): a towers-due hand shows up to maxTowerCardsPerOffer types, leaving a slot for a due Upgrade
  // (three types, or two and the Upgrade). A road piece due waits for the next hand: road stays due until laid.
  const towers = step.towersDue ? Math.min(cards.maxTowerCardsPerOffer, supply.towerTypes, cards.offerSize - (upgrade ? 1 : 0)) : 0;
  const road = step.roadDue ? Math.max(0, Math.min(cards.offerSize - towers - (upgrade ? 1 : 0), supply.roadShapes)) : 0;
  const due: HandPlan = { towers, upgrade, road, borrowed: null, borrowedEarly: 0 };
  const dealt = realCards(due);
  if (dealt === 0) return null;
  if (dealt >= MIN_CHOICE) return due;
  // One card due: borrow the first kind, in this order, that is not in the hand, is on the board and is not already
  // taken early (a negative count is a card taken ahead of its pace that the pace has not caught up with).
  const next = step.next;
  const candidates: readonly (readonly [CardKind, boolean, number, number])[] = [
    [CardKind.Path, road === 0 && supply.roadShapes > 0, next.sinceRoad, intervals.road],
    [CardKind.Tower, towers === 0 && supply.towerTypes > 0, next.sinceTowers, intervals.towers],
    [CardKind.Upgrade, !upgrade && supply.upgradeable, next.sinceUpgrade, intervals.upgrade],
  ];
  const found = candidates.find(([, open, since]) => open && since >= 0);
  if (found === undefined) return null;
  const [kind, , since, interval] = found;
  return {
    towers: towers + (kind === CardKind.Tower ? BORROWED_CARDS : 0),
    upgrade: upgrade || kind === CardKind.Upgrade,
    road: road + (kind === CardKind.Path ? BORROWED_CARDS : 0),
    // Offers until it would have been due (≥ 1: a kind due on this offer is dealt, not borrowed).
    borrowed: kind, borrowedEarly: Math.max(1, interval - since),
  };
}

/** A hand's road, tower and upgrade cards by kind. */
export interface HandMix { readonly towers: number; readonly upgrade: boolean; readonly road: number }

/** What an in-run offer was due when it opened (Offer.towersDue / upgradeDue / roadDue), for planning its reroll. */
export interface DueKinds { readonly towers: boolean; readonly upgrade: boolean; readonly road: boolean }

/**
 * The hand a REROLL of an in-run offer deals (DESIGN §4.4, M46; Kevin 2026-10-16: "Rerolls are often still giving the
 * same items (i.e. upgrade / 2 paths turns into upgrade and 2 paths), we should make it so that rerolls can give
 * towers"). Pure: `roll(n)` is the run's random stream (0 ≤ result < n), drawn only when there is more than one option.
 *
 * - The hand keeps its size (`old`'s real cards) and is planned afresh instead of dealing the old plan again.
 * - Tower cards are always open to it while a type can be placed (`supply.towerTypes`, 0 when no cell takes a tower),
 *   even on a hand that was due none; when the old hand had none and a type can be placed, the reroll deals at least one.
 * - Its mix of kinds differs from the old hand's whenever another mix is possible (the cards are then drawn excluding
 *   the old ones, as before, so even the same mix shows other cards where the pool has them).
 * - At most one kind ahead of its pace, as with a fresh hand: that kind is the plan's `borrowed` one, so taking it sets
 *   its pace back by the offers it was early (draftFlow.paceAfterTaking) and the reroll does not hand out towers or
 *   Upgrades faster than the pace. A road or Upgrade already taken early (count < 0) is not borrowed again; a tower is
 *   (Kevin asked that a reroll can always give one) and its count goes further back. The due kinds may be dropped:
 *   road and the Upgrade stay due until taken, so they come back on the next hand.
 * - null when nothing fits a hand of that size (the caller deals the old plan again).
 */
export function rerollPlan(cards: CardsData, old: HandMix, due: DueKinds, counters: PaceStep["next"], intervals: PaceIntervals,
  supply: HandSupply, roll: (n: number) => number): HandPlan | null {
  const size = old.towers + (old.upgrade ? 1 : 0) + old.road;
  const maxTowers = Math.min(cards.maxTowerCardsPerOffer, supply.towerTypes, size);
  const towersOpen = maxTowers > 0;
  type Mix = { towers: number; upgrade: boolean; road: number; borrowed: CardKind | null };
  const options: Mix[] = [];
  for (let towers = 0; towers <= maxTowers; towers++) {
    for (const upgrade of supply.upgradeable ? [false, true] : [false]) {
      const road = size - towers - (upgrade ? 1 : 0);
      if (road < 0 || road > supply.roadShapes) continue;
      // The kinds dealt ahead of their pace: one at most, and road or the Upgrade only while not already taken early.
      const early: CardKind[] = [];
      if (towers > 0 && !due.towers) early.push(CardKind.Tower);
      if (upgrade && !due.upgrade) { if (counters.sinceUpgrade < 0) continue; early.push(CardKind.Upgrade); }
      if (road > 0 && !due.road) { if (counters.sinceRoad < 0) continue; early.push(CardKind.Path); }
      if (early.length > 1) continue;
      options.push({ towers, upgrade, road, borrowed: early.length === 1 ? early[0] : null });
    }
  }
  if (options.length === 0) return null;
  const same = (m: Mix) => m.towers === old.towers && m.upgrade === old.upgrade && m.road === old.road;
  // Best first: a tower card when one can be placed, then a mix other than the old hand's.
  const score = (m: Mix) => (towersOpen && m.towers > 0 ? 2 : 0) + (same(m) ? 0 : 1);
  const best = Math.max(...options.map(score));
  const top = options.filter((m) => score(m) === best);
  const pick = top.length === 1 ? top[0] : top[roll(top.length)];
  let borrowedEarly = 0;
  if (pick.borrowed !== null) {
    const [since, interval] = pick.borrowed === CardKind.Tower ? [counters.sinceTowers, intervals.towers]
      : pick.borrowed === CardKind.Upgrade ? [counters.sinceUpgrade, intervals.upgrade] : [counters.sinceRoad, intervals.road];
    borrowedEarly = Math.max(1, interval - since);
  }
  return { towers: pick.towers, upgrade: pick.upgrade, road: pick.road, borrowed: pick.borrowed, borrowedEarly };
}
